/**
 * v050-u6a (B-602) — la VUE d'accueil par profil de l'utilisateur, dans l'espace ouvert.
 *
 * Persistée côté serveur (`/users/me/preferences/profile`, par utilisateur ET par espace, sur le
 * modèle de la préférence de navigation). Tant que rien n'est choisi, une vue SUGGÉRÉE d'après
 * le rôle d'accès est proposée — et dite comme telle (`choisie = false`). Le rôle d'accès reste
 * l'indicateur des DROITS ; la vue ne les change pas.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { ApiError, api, type ProfileId, type ProfilePreference, type ProfileView } from '@/api/client'
import { useEffectiveSession } from '@/hooks/useEffectiveSession'
import { useWorkspace } from '@/hooks/useWorkspace'
import { useT } from '@/i18n/language'
import { PROFILS, estVue, roleAcces, vueSuggeree, type RoleAcces } from '@/lib/profils'

/** Un changement de préférence, appliqué à la valeur COURANTE du cache au moment où il part. */
type Changement = { vue: ProfileView } | { profil: ProfileId; coche: boolean }

/** Pur : la préférence qui résulte d'un changement (profils toujours dans l'ordre canonique). */
export function appliquerChangement(courant: ProfilePreference | undefined, c: Changement,
                                    vueParDefaut: ProfileView): { view: ProfileView; profiles: ProfileId[] } {
  const profils = courant?.profiles ?? []
  const vue = courant && estVue(courant.view) ? courant.view : vueParDefaut
  if ('vue' in c) return { view: c.vue, profiles: profils }
  const suivants = c.coche ? [...profils, c.profil] : profils.filter((p) => p !== c.profil)
  return { view: vue, profiles: PROFILS.filter((p) => suivants.includes(p)) }
}

function estVersionPerimee(error: unknown): boolean {
  if (!(error instanceof ApiError) || error.status !== 409) return false
  try {
    return (error.detail ? (JSON.parse(error.detail) as { code?: string }).code : undefined) === 'STALE_VERSION'
  } catch { return false }
}

export interface EtatProfil {
  /** La vue affichée : choisie, sinon suggérée. */
  vue: ProfileView
  /** Vrai si la vue vient d'un choix enregistré dans cet espace. */
  choisie: boolean
  /** Profils cumulés déclarés (lus par la vue combinée). */
  profils: ProfileId[]
  role: RoleAcces
  pret: boolean
  enregistrement: boolean
  erreur: string | null
  setVue: (vue: ProfileView) => void
  /** Cocher ou décocher un profil cumulé (vue combinée). */
  basculerProfil: (profil: ProfileId, coche: boolean) => void
  /** Revenir à la vue suggérée (efface le choix enregistré dans cet espace). */
  revenirALaSuggestion: () => void
}

export function useProfil(): EtatProfil {
  const { t } = useT()
  const session = useEffectiveSession()
  const { activeId: workspaceId } = useWorkspace()
  const client = useQueryClient()
  const [erreur, setErreur] = useState<string | null>(null)
  const role = roleAcces(session?.roles ?? [])
  const cle = ['profile-preference', workspaceId, session?.userId] as const

  // La vue affichée quand rien n'est choisi : la suggestion (utilisée comme base d'un changement).
  const vueCourante: ProfileView = vueSuggeree(role)

  const preference = useQuery({
    queryKey: cle,
    queryFn: () => api.getProfilePreference(workspaceId!),
    enabled: Boolean(workspaceId) && Boolean(session),
    staleTime: 60_000,
  })

  // Les choix d'une même personne s'enchaînent (critique U6a r1 Mo-1, r2) : `scope` sérialise les
  // mutations. Le `onMutate` d'une mutation en file s'exécute tout de suite, mais son envoi attend,
  // et le `onSuccess` de la précédente réécrit le cache AVANT cet envoi. Donc l'envoi REJOUE son
  // changement sur la valeur du cache à ce moment (idempotent : poser une vue, fixer l'appartenance
  // d'un profil) avec sa version — relire le cache seul perdait silencieusement le second choix.
  const SCOPE = 'profile-preference'
  const autreEnFile = (): boolean => client.getMutationCache()
    .findAll({ predicate: (m) => m.options.scope?.id === SCOPE && m.state.status === 'pending' }).length > 1
  const enregistrer = useMutation({
    meta: { retour: 'insitu' },
    scope: { id: SCOPE },
    mutationFn: (c: Changement) => {
      const courant = client.getQueryData<ProfilePreference>(cle)
      const s = appliquerChangement(courant, c, vueCourante)
      return api.setProfilePreference(workspaceId!, { ...s, expectedVersion: courant?.version })
    },
    onMutate: async (c: Changement) => {
      setErreur(null)
      await client.cancelQueries({ queryKey: cle })
      const avant = client.getQueryData<ProfilePreference>(cle)
      const suivante = appliquerChangement(avant, c, vueCourante)
      client.setQueryData<ProfilePreference>(cle, { ...suivante, version: avant?.version ?? 0 })
      return { avant }
    },
    onError: (error, _v, contexte) => {
      if (contexte?.avant) client.setQueryData(cle, contexte.avant)
      setErreur(estVersionPerimee(error)
        ? t('profil.erreurConcurrente', 'Votre vue a été changée ailleurs entre-temps : la valeur enregistrée est affichée, choisissez à nouveau.')
        : error instanceof Error ? error.message : String(error))
      // Un autre choix attend son tour : son PUT rendra l'état serveur ; relire maintenant ferait
      // courir un GET tardif contre lui (critique U6a r3). Sinon, on relit la valeur enregistrée.
      if (!autreEnFile()) void client.invalidateQueries({ queryKey: cle })
    },
    // Un autre choix attend son tour : on ne reporte que la VERSION rendue (le cache montre déjà ce
    // choix, en optimiste) — sinon l'écran retombait un instant sur la valeur précédente.
    onSuccess: (enregistree) => {
      if (autreEnFile()) client.setQueryData<ProfilePreference>(cle, (c) => (c ? { ...c, version: enregistree.version } : enregistree))
      else client.setQueryData(cle, enregistree)
    },
  })

  const effacer = useMutation({
    meta: { retour: 'insitu' },
    scope: { id: SCOPE },
    mutationFn: () => api.resetProfilePreference(workspaceId!),
    onSuccess: () => { client.setQueryData<ProfilePreference>(cle, { view: null, profiles: [], version: 0 }) },
    onError: (error) => { setErreur(error instanceof Error ? error.message : String(error)) },
  })

  const donnees = preference.data
  const choisie = Boolean(donnees && estVue(donnees.view))
  const vue: ProfileView = choisie ? (donnees!.view as ProfileView) : vueSuggeree(role)
  const profils = donnees?.profiles ?? []

  return {
    vue,
    choisie,
    profils,
    role,
    pret: !workspaceId || preference.isSuccess || preference.isError,
    enregistrement: enregistrer.isPending || effacer.isPending,
    erreur,
    setVue: (suivante) => { if (workspaceId) enregistrer.mutate({ vue: suivante }) },
    basculerProfil: (profil, coche) => { if (workspaceId) enregistrer.mutate({ profil, coche }) },
    revenirALaSuggestion: () => { if (workspaceId) effacer.mutate() },
  }
}
